import React, { useRef, useState } from 'react';
import { Dialog, DialogContent, DialogActions, Button, Typography, Box, IconButton, Tooltip, Slide, Avatar, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useModalTesoreroController } from './ModalTesorero/hooks/useModalTesoreroController';
import { SectionCard } from './ModalSolicitudes/components/SectionCard';
import SimpleSuccessSpinner from './LoadingSpinner';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const elegantInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5, backgroundColor: '#fbfbfd', transition: 'box-shadow .15s, border-color .15s',
    '& fieldset': { borderColor: 'rgba(0,0,0,0.12)' },
    '&:hover fieldset': { borderColor: 'rgba(0,0,0,0.2)' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5' },
    '&.Mui-focused': { boxShadow: '0 0 0 2px rgba(6,85,165,0.10)' },
  },
  '& .MuiInputLabel-root': { fontWeight: 600 },
  '& .MuiFormHelperText-root': { ml: 0 },
};

export default function ModalTesorero({ open, handleCloseModal, selectedRowData, onSaved }) {
  const {
    paymentDetails, handleInputChange, files, setFiles, metodosPago,
    combinedErrors, setCombinedErrors, loading, autorizarPago, compRules,
    referenciasArray, handleReferenciaArrayChange, addReferenciaField, removeReferenciaField
  } = useModalTesoreroController(open, handleCloseModal, selectedRowData, onSaved);

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  
  const selectedMetodo = metodosPago.find(m => String(m.METODOPAGOID) === String(paymentDetails.metodoPago));
  
  const reqComp = compRules.showSection;
  const compMandatory = compRules.isTransfer;

  const reqRef = Number(selectedMetodo?.REQUIERE_REFERENCIA) === 1;

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (dropped.length) {
      setFiles(p => {
        const existing = new Set(p.map(f => `${f.name}__${f.size}`));
        return [...p, ...dropped.filter(f => !existing.has(`${f.name}__${f.size}`))];
      });
      setCombinedErrors(p => { const u = { ...p }; delete u.files; return u; });
    }
  };

  const removeFile = (idx) => setFiles(p => p.filter((_, i) => i !== idx));

  return (
    <>
      <Dialog open={open} disableRestoreFocus onClose={handleCloseModal} maxWidth="md" fullWidth TransitionComponent={Transition} PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 24px 64px rgba(0,0,0,.2)', bgcolor: '#f8fafc' } }}>
        <Box sx={{ px: 2.5, py: 1.5, background: 'linear-gradient(135deg, #0655A5 0%, #033C75 100%)', color: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', width: 42, height: 42 }}>
            <PaymentRoundedIcon />
          </Avatar>
          
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>Registro de Pago</Typography>
            {selectedRowData && (() => {
              const rawMonto = selectedRowData.MONTOSOLICITADO ?? selectedRowData.MONTO_SOLICITADO ?? selectedRowData.TOTAL ?? selectedRowData.IMPORTE ?? 0;
              const numMonto = typeof rawMonto === 'string' 
                ? Number(rawMonto.replace(/[^0-9.-]/g, '')) 
                : Number(rawMonto);
              const montoVal = isNaN(numMonto) ? 0 : numMonto;

              const cc = selectedRowData.NUMEROCC || selectedRowData.CENTROCOSTO || selectedRowData.NUMERO_CC || selectedRowData.NUMERO_CENTRO_COSTOS || selectedRowData.CC || '';
              const prov = selectedRowData.PROVEEDOR || paymentDetails.proveedor || '';

              return (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.2, mt: 0.5, fontSize: '0.78rem', opacity: 0.95 }}>
                  <Typography variant="caption" sx={{ bgcolor: 'rgba(255,255,255,0.18)', px: 1, py: 0.2, borderRadius: 1, fontWeight: 700 }}>
                    OC: {selectedRowData.FOLIO_OC || selectedRowData.FOLIO || selectedRowData.ID || 'N/A'}
                  </Typography>

                  {(selectedRowData.EMPRESA || selectedRowData.NOMBRE_EMPRESA) && (
                    <Typography variant="caption" sx={{ borderLeft: '1px solid rgba(255,255,255,0.3)', pl: 1.2, fontWeight: 600 }}>
                      {selectedRowData.EMPRESA || selectedRowData.NOMBRE_EMPRESA}
                    </Typography>
                  )}

                  {cc && (
                    <Typography variant="caption" sx={{ borderLeft: '1px solid rgba(255,255,255,0.3)', pl: 1.2, fontWeight: 700, color: '#ffe082' }}>
                      CC: {cc}
                    </Typography>
                  )}

                  {prov && (
                    <Typography variant="caption" sx={{ borderLeft: '1px solid rgba(255,255,255,0.3)', pl: 1.2, fontWeight: 600 }} title={prov}>
                      Prov: {prov}
                    </Typography>
                  )}

                  <Typography variant="caption" sx={{ borderLeft: '1px solid rgba(255,255,255,0.3)', pl: 1.2, fontWeight: 800, color: '#90ef90' }}>
                    Monto: {new Intl.NumberFormat('es-MX', { style: 'currency', currency: selectedRowData.DIVISA || 'MXN' }).format(montoVal)}
                  </Typography>
                </Box>
              );
            })()}
          </Box>

          <Tooltip title="Cerrar"><IconButton onClick={handleCloseModal} sx={{ color: 'white' }}><CloseIcon /></IconButton></Tooltip>
        </Box>

        <DialogContent sx={{ p: 3 }}>

          <SectionCard title="Datos de pago" subtitle="Fecha y método" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField size="small" type="date" label="Fecha de Pago" name="fechaPago" value={paymentDetails.fechaPago} onChange={handleInputChange} fullWidth InputLabelProps={{ shrink: true }} sx={{ ...elegantInputSx, flex: { xs: '100%', sm: '1 1 48%' } }} error={Boolean(combinedErrors.fechaPago)} helperText={combinedErrors.fechaPago || ' '} />
              <FormControl size="small" fullWidth sx={{ ...elegantInputSx, flex: { xs: '100%', sm: '1 1 48%' } }} error={Boolean(combinedErrors.metodoPago)}>
                <InputLabel>Método de Pago</InputLabel>
                <Select name="metodoPago" value={paymentDetails.metodoPago} onChange={handleInputChange} label="Método de Pago" MenuProps={{ disableRestoreFocus: true }}>
                  {metodosPago.map(m => <MenuItem key={m.METODOPAGOID} value={m.METODOPAGOID}>{m.METODOPAGO}</MenuItem>)}
                </Select>
                {combinedErrors.metodoPago && <Typography variant="caption" color="error">{combinedErrors.metodoPago}</Typography>}
              </FormControl>
            </Box>
            {selectedMetodo && (
              <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                <Chip size="small" label={reqComp ? 'Requiere comprobante' : 'No requiere comprobante'} color={reqComp ? 'primary' : 'default'} />
                {reqRef && <Chip size="small" label="Requiere referencia" color="warning" />}
              </Box>
            )}
          </SectionCard>

          {reqRef && (
            <SectionCard 
              title="Referencias (Máx. 255 caracteres)" 
              sx={{ mb: 2 }} 
              error={Boolean(combinedErrors.referencia) || paymentDetails.referencia.length > 255}
              action={
                <Tooltip title="Agregar otra referencia">
                  <IconButton size="small" onClick={addReferenciaField} color="primary">
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Tooltip>
              }
            >
              <Stack spacing={1.5}>
                {referenciasArray.map((ref, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField 
                      size="small" 
                      label={`Referencia ${referenciasArray.length > 1 ? idx + 1 : ''}`} 
                      value={ref} 
                      onChange={(e) => handleReferenciaArrayChange(idx, e.target.value)} 
                      fullWidth 
                      sx={elegantInputSx} 
                      error={Boolean(combinedErrors.referencia)} 
                    />
                    {referenciasArray.length > 1 && (
                      <IconButton size="small" onClick={() => removeReferenciaField(idx)} color="error">
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}
                
                {/* Contador y Advertencia */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: paymentDetails.referencia.length > 255 ? '#d32f2f' : '#666' }}>
                    Caracteres totales: {paymentDetails.referencia.length} / 255
                  </Typography>
                  {paymentDetails.referencia.length > 255 && (
                    <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 800 }}>
                      ⚠️ Límite excedido (Se truncará en BD)
                    </Typography>
                  )}
                </Box>
              </Stack>
            </SectionCard>
          )}

          <SectionCard title="Comentarios" sx={{ mb: 2 }}>
            <TextField size="small" multiline rows={3} label="Comentarios" name="observations" value={paymentDetails.observations} onChange={handleInputChange} fullWidth sx={elegantInputSx} />
          </SectionCard>

          {reqComp && (
            <SectionCard 
              title="Comprobante de Pago" 
              subtitle={compMandatory ? "Suba el comprobante para procesar la transferencia" : "Agregue el comprobante si lo tiene disponible"} 
              error={combinedErrors.files}
              sx={{
                bgcolor: compMandatory ? 'rgba(239, 83, 80, 0.02)' : 'rgba(3, 169, 244, 0.02)',
                border: '1px solid',
                borderColor: compMandatory ? 'rgba(239, 83, 80, 0.3)' : 'rgba(3, 169, 244, 0.3)',
              }}
              action={
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {compMandatory ? (
                    <Chip label="¡OBLIGATORIO!" color="error" sx={{ fontWeight: 900, fontSize: '0.75rem', px: 1, height: 28, boxShadow: '0 2px 4px rgba(211, 47, 47, 0.2)' }} />
                  ) : (
                    <Chip label="OPCIONAL" color="info" variant="outlined" sx={{ fontWeight: 800, fontSize: '0.75rem', px: 1, height: 28 }} />
                  )}
                </Box>
              }
            >
              <Box
                onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
                onDrop={handleDrop}
                sx={{
                  mt: 1,
                  p: 3,
                  border: '2px dashed',
                  borderColor: isDragging 
                    ? '#09814A' 
                    : (combinedErrors.files ? '#ef5350' : (compMandatory ? 'rgba(211, 47, 47, 0.25)' : 'rgba(2, 136, 209, 0.25)')),
                  borderRadius: 3,
                  bgcolor: isDragging 
                    ? 'rgba(9, 129, 74, 0.05)' 
                    : (compMandatory ? 'rgba(211, 47, 47, 0.015)' : 'rgba(2, 136, 209, 0.015)'),
                  textAlign: 'center',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: isDragging ? 'rgba(9, 129, 74, 0.08)' : (compMandatory ? 'rgba(211, 47, 47, 0.03)' : 'rgba(2, 136, 209, 0.03)'),
                  }
                }}
              >
                <input 
                  type="file" 
                  multiple 
                  ref={fileInputRef} 
                  style={{ display: 'none' }} 
                  onChange={(e) => {
                    const arr = Array.from(e.target.files || []);
                    if (arr.length) {
                      setFiles(p => {
                        const existing = new Set(p.map(f => `${f.name}__${f.size}`));
                        return [...p, ...arr.filter(f => !existing.has(`${f.name}__${f.size}`))];
                      });
                      setCombinedErrors(p => { const u = { ...p }; delete u.files; return u; });
                    }
                    e.target.value = '';
                  }} 
                />
                
                <UploadFileIcon sx={{ fontSize: 40, color: compMandatory ? 'rgba(211, 47, 47, 0.4)' : 'rgba(2, 136, 209, 0.4)', mb: 1.5 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: compMandatory ? 'rgba(183, 28, 28, 0.7)' : 'rgba(1, 87, 155, 0.7)', mb: 0.5, letterSpacing: 0.8, fontSize: '0.75rem' }}>
                    {compMandatory ? 'COMPROBANTE REQUERIDO' : 'COMPROBANTE OPCIONAL'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, opacity: 0.7 }}>
                    Arrastra tus archivos aquí o usa el botón
                  </Typography>
                </Box>

                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<AttachFileIcon />} 
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ 
                    borderRadius: 4, 
                    textTransform: 'none', 
                    fontWeight: 600,
                    px: 3,
                    fontSize: '0.75rem',
                    borderColor: compMandatory ? 'rgba(211, 47, 47, 0.3)' : 'rgba(2, 136, 209, 0.3)',
                    color: compMandatory ? 'rgba(211, 47, 47, 0.7)' : 'rgba(2, 136, 209, 0.7)',
                    '&:hover': {
                        borderColor: compMandatory ? 'rgba(211, 47, 47, 0.6)' : 'rgba(2, 136, 209, 0.6)',
                        bgcolor: compMandatory ? 'rgba(211, 47, 47, 0.03)' : 'rgba(2, 136, 209, 0.03)'
                    }
                  }}
                >
                  {files.length > 0 ? 'Agregar más' : 'Seleccionar archivos'}
                </Button>

                {combinedErrors.files && (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 2, fontWeight: 700 }}>
                    ⚠️ {combinedErrors.files}
                  </Typography>
                )}

                {files.length > 0 && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}>
                      ARCHIVOS ADJUNTOS ({files.length}):
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
                      {files.map((f, idx) => (
                        <Chip 
                          key={idx} 
                          label={f.name} 
                          onDelete={() => removeFile(idx)} 
                          size="small"
                          color="primary" 
                          variant="outlined" 
                          sx={{ borderRadius: 1.5, fontWeight: 600, maxWidth: 200 }} 
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </SectionCard>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #eee', bgcolor: '#fbfbfd' }}>
          <Button onClick={handleCloseModal} sx={{ color: 'text.secondary', fontWeight: 600 }}>Cancelar</Button>
          <Button variant="contained" onClick={autorizarPago} disabled={loading} sx={{ px: 3, borderRadius: 8, boxShadow: '0 4px 12px rgba(6,85,165,0.3)' }}>
            Registrar Pago
          </Button>
        </DialogActions>
      </Dialog>
      <SimpleSuccessSpinner open={loading} handleClose={() => null} message="Registrando el pago..." />
    </>
  );
}